'use client';

import { c as _c } from "react/compiler-runtime";
import { ArrowLeft } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from '@/i18n/navigation';
import { NumberedTechnology, RadarVisualization } from './RadarVisualization';
import { technologies } from './data';
import { QUADRANTS, QUADRANT_LABELS, Quadrant, RINGS, RING_LABELS, Ring } from './types';
const RING_MEANINGS: Record<Ring, string> = {
  adopt: 'Strong confidence for widespread use',
  trial: 'Worth pursuing in projects',
  assess: 'Worth exploring and understanding',
  hold: 'Proceed with caution'
};

// Numbered by quadrant, then ring, then name, so the radar and the list agree
const numbered: NumberedTechnology[] = [...technologies].sort((a, b) => QUADRANTS.indexOf(a.quadrant) - QUADRANTS.indexOf(b.quadrant) || RINGS.indexOf(a.ring) - RINGS.indexOf(b.ring) || a.name.localeCompare(b.name)).map((tech, index) => ({
  ...tech,
  number: index + 1
}));
const countBy = (test: (tech: NumberedTechnology) => boolean) => numbered.filter(test).length;
interface TechnologyRadarContentProps {
  /** Where the back link above the title goes; omitted when there is none */
  backHref?: string;
  backLabel?: string;
}
export function TechnologyRadarContent(t0) {
  const $ = _c(58);
  const {
    backHref,
    backLabel
  } = t0;
  const [selectedRing, setSelectedRing] = useState(null);
  const [selectedQuadrant, setSelectedQuadrant] = useState(null);
  let t1;
  if ($[0] !== selectedQuadrant || $[1] !== selectedRing) {
    t1 = numbered.filter(tech => {
      if (selectedRing && tech.ring !== selectedRing) {
        return false;
      }
      if (selectedQuadrant && tech.quadrant !== selectedQuadrant) {
        return false;
      }
      return true;
    });
    $[0] = selectedQuadrant;
    $[1] = selectedRing;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const filteredTechnologies = t1;
  let t2;
  if ($[3] !== filteredTechnologies) {
    t2 = new Set(filteredTechnologies.map(_temp));
    $[3] = filteredTechnologies;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const shown = t2;
  let t3;
  if ($[5] !== shown) {
    t3 = new Set(numbered.filter(t => !shown.has(t.id)).map(_temp2));
    $[5] = shown;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const hiddenIds = t3;
  let t4;
  if ($[7] !== backHref || $[8] !== backLabel) {
    t4 = backHref && <Link href={backHref} className="back"><ArrowLeft aria-hidden="true" />{backLabel}</Link>;
    $[7] = backHref;
    $[8] = backLabel;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="k">Demo · Interactive visualization</div>;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <h1 className="md">Technology Radar<span aria-hidden="true">.</span></h1>;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div>{t5}{t6}<p className="lede">A visual representation of technology choices and recommendations inspired by{" "}<a href="https://www.thoughtworks.com/en-us/radar" target="_blank" rel="noopener noreferrer">Thoughtworks Technology Radar</a>.</p></div>;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <header className="head">{t7}<dl className="stats"><div><dt>Technologies</dt><dd>{numbered.length}</dd></div><div><dt>To adopt</dt><dd>{countBy(_temp3)}</dd></div><div><dt>New</dt><dd>{countBy(_temp4)}</dd></div></dl></header>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="k">Ring</span>;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setSelectedRing(null);
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = selectedRing === null ? "chip on" : "chip";
  const t12 = selectedRing === null;
  let t13;
  if ($[16] !== t11 || $[17] !== t12) {
    t13 = <button type="button" onClick={t10} className={t11} aria-pressed={t12}>All</button>;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== selectedRing) {
    t14 = RINGS.map(ring => <button key={ring} type="button" onClick={() => setSelectedRing(ring)} className={selectedRing === ring ? "chip on" : "chip"} aria-pressed={selectedRing === ring}>{RING_LABELS[ring]} {countBy(tech_3 => tech_3.ring === ring)}</button>);
    $[19] = selectedRing;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t13 || $[22] !== t14) {
    t15 = <div className="chips" role="group" aria-label="Filter by ring">{t9}{t13}{t14}</div>;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className="k">Quadrant</span>;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setSelectedQuadrant(null);
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  const t18 = selectedQuadrant === null ? "chip on" : "chip";
  const t19 = selectedQuadrant === null;
  let t20;
  if ($[26] !== t18 || $[27] !== t19) {
    t20 = <button type="button" onClick={t17} className={t18} aria-pressed={t19}>All</button>;
    $[26] = t18;
    $[27] = t19;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  let t21;
  if ($[29] !== selectedQuadrant) {
    t21 = QUADRANTS.map(quadrant => <button key={quadrant} type="button" onClick={() => setSelectedQuadrant(quadrant)} className={selectedQuadrant === quadrant ? "chip on" : "chip"} aria-pressed={selectedQuadrant === quadrant}>{QUADRANT_LABELS[quadrant]}{" "}{countBy(tech_4 => tech_4.quadrant === quadrant)}</button>);
    $[29] = selectedQuadrant;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[31] !== t20 || $[32] !== t21) {
    t22 = <div className="chips" role="group" aria-label="Filter by quadrant">{t16}{t20}{t21}</div>;
    $[31] = t20;
    $[32] = t21;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== t15 || $[35] !== t22) {
    t23 = <section className="sec tight filters">{t15}{t22}</section>;
    $[34] = t15;
    $[35] = t22;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== hiddenIds) {
    t24 = <RadarVisualization technologies={numbered} hiddenIds={hiddenIds} />;
    $[37] = hiddenIds;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <h2 className="k">How to read it</h2>;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  let t27;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <ul className="rings">{RINGS.map(_temp5)}</ul>;
    t27 = <h2 className="k">Indicators</h2>;
    $[40] = t26;
    $[41] = t27;
  } else {
    t26 = $[40];
    t27 = $[41];
  }
  let t28;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <aside>{t25}{t26}{t27}<ul className="indicators"><li><span className="dot new" aria-hidden="true" />New technology</li><li><span className="dot" aria-hidden="true" />Existing technology</li></ul><p>Closer to the centre means more confidence. Numbers match the list below. Select a quadrant name to jump to its list.</p></aside>;
    $[42] = t28;
  } else {
    t28 = $[42];
  }
  let t29;
  if ($[43] !== t24) {
    t29 = <section className="stage">{t24}{t28}</section>;
    $[43] = t24;
    $[44] = t29;
  } else {
    t29 = $[44];
  }
  let t30;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className="k">The full list</div>;
    $[45] = t30;
  } else {
    t30 = $[45];
  }
  let t31;
  if ($[46] !== filteredTechnologies.length) {
    t31 = <h2>Technologies ({filteredTechnologies.length})</h2>;
    $[46] = filteredTechnologies.length;
    $[47] = t31;
  } else {
    t31 = $[47];
  }
  let t32;
  if ($[48] !== filteredTechnologies) {
    t32 = QUADRANTS.map((quadrant_0, index) => {
      const quadrantTechnologies = filteredTechnologies.filter(tech_6 => tech_6.quadrant === quadrant_0);
      if (quadrantTechnologies.length === 0) {
        return null;
      }
      return <div className="split group" key={quadrant_0}><div className="side"><div className="k">{String(index + 1).padStart(2, "0")}</div><h3 id={`quadrant-${quadrant_0}`}>{QUADRANT_LABELS[quadrant_0]}</h3><div className="k">{quadrantTechnologies.length}{" "}{quadrantTechnologies.length === 1 ? "technology" : "technologies"}</div></div><ul className="main techs">{quadrantTechnologies.map(_temp6)}</ul></div>;
    });
    $[48] = filteredTechnologies;
    $[49] = t32;
  } else {
    t32 = $[49];
  }
  let t33;
  if ($[50] !== t31 || $[51] !== t32) {
    t33 = <section className="sec">{t30}{t31}{t32}</section>;
    $[50] = t31;
    $[51] = t32;
    $[52] = t33;
  } else {
    t33 = $[52];
  }
  let t34;
  if ($[53] !== t23 || $[54] !== t29 || $[55] !== t33 || $[56] !== t4) {
    t34 = <div className="landing-root"><div className="l-page l-radar"><div className="wrap">{t4}{t8}{t23}{t29}{t33}</div></div></div>;
    $[53] = t23;
    $[54] = t29;
    $[55] = t33;
    $[56] = t4;
    $[57] = t34;
  } else {
    t34 = $[57];
  }
  return t34;
}
function _temp6(tech_7) {
  return <li key={tech_7.id} id={`tech-${tech_7.id}`}><span className={tech_7.isNew ? "dot new" : "dot"} aria-hidden="true">{tech_7.number}</span><div><div className="line"><h4>{tech_7.link ? <a href={tech_7.link} target="_blank" rel="noopener noreferrer">{tech_7.name}</a> : tech_7.name}</h4><span className="k">{RING_LABELS[tech_7.ring]}</span>{tech_7.isNew && <span className="k new">NEW</span>}</div><p>{tech_7.description}</p></div></li>;
}
function _temp5(ring_0) {
  return <li key={ring_0}><div><span className="name">{RING_LABELS[ring_0]}</span><span className="k">{countBy(tech_5 => tech_5.ring === ring_0)}</span></div><p>{RING_MEANINGS[ring_0]}</p></li>;
}
function _temp4(tech_2) {
  return tech_2.isNew;
}
function _temp3(tech_1) {
  return tech_1.ring === "adopt";
}
function _temp2(t_0) {
  return t_0.id;
}
function _temp(tech_0) {
  return tech_0.id;
}